import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen, CalendarCheck, Check, ChevronDown, CircleCheck, Layers, PartyPopper, RotateCw, SlidersHorizontal, Sparkles,
} from 'lucide-react';
import { flashcardProgressService, apiErrorMessage } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import { AppShell, Button, EmptyState, Loader, PageHeader, ProgressBar, ScoreCard } from '../components/ui';
import '../components/FlashcardViewer.css';
import './FlashcardReview.css';

// Mesma escala SM-2 do visualizador de flashcards do tópico
const RATINGS = [
  { quality: 0, label: 'Esqueci', tone: 'danger' },
  { quality: 1, label: 'Errei', tone: 'warning' },
  { quality: 3, label: 'Difícil', tone: 'neutral' },
  { quality: 4, label: 'Bom', tone: 'navy' },
  { quality: 5, label: 'Fácil', tone: 'lime' },
];
const NEW_LIMITS = [5, 10, 20];

/** Revisão diária: cartões vencidos de todos os tópicos do curso + alguns novos por dia. */
function FlashcardReview() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useCourse();
  const [queue, setQueue] = useState(null);
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({ reviewed: 0, mastered: 0 });
  const [newLimit, setNewLimit] = useState(10);
  const [error, setError] = useState(null);
  const [limitPopoverOpen, setLimitPopoverOpen] = useState(false);
  const limitRef = useRef(null);

  useEffect(() => { selectCourse(courseSlug); }, [courseSlug, selectCourse]);

  const load = useCallback(async (limit) => {
    setQueue(null);
    setError(null);
    try {
      const q = await flashcardProgressService.getReviewQueue(courseSlug, limit);
      setQueue(q);
      setCards([...q.due, ...q.fresh]);
      setIndex(0);
      setFlipped(false);
      setDone({ reviewed: 0, mastered: 0 });
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, [courseSlug]);

  useEffect(() => { load(newLimit); }, [load, newLimit]);

  const card = cards[index];
  const finished = queue && index >= cards.length;

  const advance = (field) => {
    setDone(d => ({ ...d, [field]: d[field] + 1 }));
    setFlipped(false);
    setIndex(i => i + 1);
  };

  const rate = async (quality) => {
    if (busy || !card) return;
    setBusy(true);
    try {
      await flashcardProgressService.reviewCard(card.topicId, card.cardIndex, courseSlug, quality);
      // "Esqueci" e "Errei" voltam para o fim da sessão de hoje
      if (quality < 3) setCards(cs => [...cs, { ...card, isNew: false }]);
      advance('reviewed');
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const master = async () => {
    if (busy || !card) return;
    setBusy(true);
    try {
      await flashcardProgressService.setMastered(card.topicId, card.cardIndex, courseSlug, true);
      advance('mastered');
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Popover do limite de novos: fecha ao clicar fora ou com Esc
  useEffect(() => {
    if (!limitPopoverOpen) return undefined;
    const onDown = (e) => { if (!limitRef.current?.contains(e.target)) setLimitPopoverOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setLimitPopoverOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [limitPopoverOpen]);

  const chooseLimit = (n) => {
    setLimitPopoverOpen(false);
    setNewLimit(n);
  };

  // Atalhos: espaço vira o cartão; 1 a 5 avaliam depois de virar
  useEffect(() => {
    const onKey = (e) => {
      if (!card || limitPopoverOpen || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setFlipped(f => !f);
      } else if (flipped && /^[1-5]$/.test(e.key)) {
        rate(RATINGS[Number(e.key) - 1].quality);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const header = (
    <PageHeader
      icon={Layers}
      eyebrow={currentCourse?.name}
      title="Revisão de flashcards"
      subtitle="Os cartões voltam no dia certo para fixar o conteúdo (repetição espaçada)."
    />
  );

  if (error && !queue) {
    return (
      <AppShell section="Flashcards" courseName={currentCourse?.name}>
        <div className="ss-page">
          {header}
          <EmptyState file="flashcards.xml" title="Não foi possível carregar a revisão" description={error} />
        </div>
      </AppShell>
    );
  }

  if (!queue) {
    return (
      <AppShell section="Flashcards" courseName={currentCourse?.name}>
        <div className="ss-page">
          {header}
          <Loader label="Montando sua revisão de hoje..." />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell section="Flashcards" courseName={currentCourse?.name}>
      <div className="ss-page">
        {header}

        <ScoreCard
          tour="fc-placar"
          inline
          items={[
            { icon: CalendarCheck, value: queue.due.length, label: 'Para revisar', hint: 'vencidos hoje' },
            { icon: Sparkles, value: queue.fresh.length, label: 'Novos hoje', hint: 'não estudados' },
            { icon: CircleCheck, value: queue.masteredCards, label: 'Dominados', hint: `de ${queue.totalCards}`, tone: 'lime' },
          ]}
        />

        {cards.length === 0 ? (
          <div className="fr-body">
            <EmptyState
              file="revisao.xml"
              title="Nada para revisar hoje"
              description={queue.totalCards === 0
                ? 'Este curso ainda não tem flashcards.'
                : 'Você está em dia. Volte amanhã ou estude flashcards dentro de um tópico.'}
              action={<Button icon={BookOpen} onClick={() => navigate(`/curso/${courseSlug}/estudo`)}>Ir para o estudo</Button>}
            />
          </div>
        ) : finished ? (
          <div className="fr-finish">
            <PartyPopper size={36} aria-hidden="true" />
            <h2>Revisão de hoje concluída!</h2>
            <p>
              {done.reviewed} {done.reviewed === 1 ? 'cartão revisado' : 'cartões revisados'}
              {done.mastered > 0 && ` · ${done.mastered} marcado(s) como dominado`}.
            </p>
            <div className="fr-finish__actions">
              <Button variant="accent" icon={Sparkles} onClick={() => setNewLimit(l => Math.min(l + 10, 50))}>Estudar mais novos</Button>
              <Button variant="outline" icon={BookOpen} onClick={() => navigate(`/curso/${courseSlug}/estudo`)}>Voltar ao estudo</Button>
            </div>
          </div>
        ) : (
          <div className="fr-session">
            <div className="fr-top">
              <span className="fr-top__count">
                {String(index + 1).padStart(2, '0')}<span> / {String(cards.length).padStart(2, '0')}</span>
              </span>
              <ProgressBar value={(index / cards.length) * 100} label="Progresso da revisão" className="fr-top__bar" />
              <div className="fr-limit" ref={limitRef} data-tour="fc-limite">
                <button
                  type="button"
                  className="fr-limit__chip"
                  aria-haspopup="dialog"
                  aria-expanded={limitPopoverOpen}
                  onClick={() => setLimitPopoverOpen(o => !o)}
                >
                  <SlidersHorizontal size={13} aria-hidden="true" />
                  {newLimit} novos/dia
                  <ChevronDown size={12} aria-hidden="true" />
                </button>
                {limitPopoverOpen && (
                  <div className="fr-limit__pop" role="dialog" aria-label="Cartões novos por dia">
                    <span className="fr-limit__title">Cartões novos por dia</span>
                    <div className="fr-limit__opts">
                      {NEW_LIMITS.map(n => (
                        <button
                          key={n}
                          type="button"
                          className={`fr-limit__opt ${newLimit === n ? 'is-on' : ''}`}
                          aria-pressed={newLimit === n}
                          onClick={() => chooseLimit(n)}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <span className="fr-limit__note">Revisões vencidas não contam no limite.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="fc-stage" data-tour="fc-cartao" onClick={() => setFlipped(!flipped)} role="button" tabIndex={0}
                 aria-label={flipped ? 'Mostrar pergunta' : 'Mostrar resposta'}>
              <div className={`fc-card ${flipped ? 'is-flipped' : ''}`}>
                <div className="fc-face fc-face--front">
                  <div className="fr-face__head">
                    <span className="fr-face__topic"><strong>Fase {card.fase}</strong>{card.topicName}</span>
                    <span className="fr-face__side">Pergunta</span>
                  </div>
                  <div className="fr-face__body">
                    <div className="fc-face__content">{card.front}</div>
                  </div>
                  <span className="fc-face__hint"><RotateCw size={14} aria-hidden="true" /> Clique no cartão para virar</span>
                </div>
                <div className="fc-face fc-face--back">
                  <div className="fr-face__head">
                    <span className="fr-face__topic"><strong>Fase {card.fase}</strong>{card.topicName}</span>
                    <span className="fr-face__side">Resposta</span>
                  </div>
                  <div className="fr-face__body">
                    <span className="fr-face__question">{card.front}</span>
                    <div className="fc-face__content">{card.back}</div>
                  </div>
                </div>
              </div>
            </div>

            {flipped ? (
              <div className="fr-rate">
                <div className="fr-rate__buttons">
                  {RATINGS.map((r, i) => (
                    <button key={r.quality} type="button" className={`fc-rate__btn fc-rate__btn--${r.tone}`}
                            onClick={() => rate(r.quality)} disabled={busy}>
                      {r.label} <kbd>{i + 1}</kbd>
                    </button>
                  ))}
                </div>
                <Button size="sm" variant="ghost" icon={Check} onClick={master} disabled={busy}>Já domino este cartão</Button>
              </div>
            ) : (
              <div className="fr-reveal">
                <Button variant="accent" size="lg" onClick={() => setFlipped(true)}>
                  Mostrar resposta <kbd className="fr-kbd">espaço</kbd>
                </Button>
              </div>
            )}
            {error && <p className="fr-error" role="alert">{error}</p>}
          </div>
        )}
      </div>
    </AppShell>
  );
}

export default FlashcardReview;
